/**
 * Stripe Identity verification helpers.
 * Status lives on the user record as `identityVerificationStatus`
 * ('unverified' | 'pending' | 'verified' | 'failed') and is kept in sync by the
 * Stripe webhook. Only paid tiers are ever prompted — Free tier sees nothing.
 */
import axios from 'axios';
import API_URL from '../config';
import { isFreeSubscription } from './subscription';

export const IDENTITY_UNVERIFIED = 'unverified';
export const IDENTITY_PENDING = 'pending';
export const IDENTITY_VERIFIED = 'verified';
export const IDENTITY_FAILED = 'failed';

/** Normalized status. Unknown / missing (e.g. a stale cached user) → 'unverified'. */
export function getIdentityStatus(user) {
  const raw = user?.identityVerificationStatus;
  if (typeof raw !== 'string') return IDENTITY_UNVERIFIED;
  const status = raw.toLowerCase().trim();
  if (
    status === IDENTITY_PENDING ||
    status === IDENTITY_VERIFIED ||
    status === IDENTITY_FAILED
  ) {
    return status;
  }
  return IDENTITY_UNVERIFIED;
}

/** Identity verification applies to Owner-Operator and Fleet/Pro only. */
export function requiresIdentityVerification(user) {
  if (!user || typeof user !== 'object') return false;
  return !isFreeSubscription(user);
}

/** Paid user who still needs to act (not already pending or verified). */
export function shouldPromptIdentityVerification(user) {
  if (!requiresIdentityVerification(user)) return false;
  const status = getIdentityStatus(user);
  return status === IDENTITY_UNVERIFIED || status === IDENTITY_FAILED;
}

export function isIdentityResolved(status) {
  return status === IDENTITY_VERIFIED || status === IDENTITY_FAILED;
}

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { headers: { Authorization: token ? `Bearer ${token}` : '' } };
}

/** POST /api/identity/create-session → Stripe-hosted verification URL. */
export async function createVerificationSession() {
  const res = await axios.post(
    `${API_URL}/api/identity/create-session`,
    {},
    getAuthHeaders(),
  );
  return res.data?.verificationUrl || null;
}

/** GET /api/auth/profile — same endpoint the dashboard uses; carries the identity fields. */
export async function fetchIdentityProfile() {
  const res = await axios.get(`${API_URL}/api/auth/profile`, getAuthHeaders());
  return { user: res.data || null, status: getIdentityStatus(res.data) };
}
